import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Card } from "@/components/shared";

const STATUS_COLOR = { active: "#FF5722", in_progress: "#3B82F6", completed: "#22C55E" };

export default function MapView() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/map/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, { scrollWheelZoom: true }).setView([39.9612, -82.9988], 10);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    const map = mapRef.current;
    layerRef.current.clearLayers();
    const pts = [];
    jobs.forEach((j) => {
      const color = STATUS_COLOR[j.status] || "#FF5722";
      const ring = j.is_today ? "0 0 0 4px rgba(255,87,34,0.4)," : "";
      const icon = L.divIcon({
        className: "", iconSize: [22, 22], iconAnchor: [11, 11],
        html: `<div style="background:${color};width:18px;height:18px;border:3px solid #000;box-shadow:${ring}2px 2px 0 #000;"></div>`,
      });
      const m = L.marker([j.lat, j.lng], { icon }).addTo(layerRef.current);
      m.bindPopup(`<div style="font-family:sans-serif;min-width:160px"><strong>${j.address}</strong><br/><span style="color:#FF5722">${j.service_type}</span><br/>${j.assigned_crew_name || "Unassigned"}${j.scheduled_date ? "<br/>📅 " + new Date(j.scheduled_date).toLocaleDateString() : ""}</div>`);
      pts.push([j.lat, j.lng]);
    });
    if (pts.length) map.fitBounds(pts, { padding: [50, 50], maxZoom: 13 });
    setTimeout(() => map.invalidateSize(), 120);
  }, [jobs]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return (
    <div className="space-y-6">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Dispatch Routing</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">Job Map</h1>
      </div>
      <div className="flex gap-4 flex-wrap font-mono text-xs">
        <Legend color="#FF5722" label="Active" />
        <Legend color="#3B82F6" label="In Progress" />
        <span className="text-zinc-500">Glowing pins = scheduled today</span>
      </div>
      <Card className="p-1 overflow-hidden">
        <div ref={containerRef} data-testid="job-map" style={{ height: "70vh", width: "100%", background: "#1a1a1d" }} />
      </Card>
      {!loading && jobs.length === 0 && <Card className="p-6 text-center text-zinc-500 font-mono">No mappable jobs yet. Addresses are geocoded automatically when you open the map.</Card>}
    </div>
  );
}

function Legend({ color, label }) {
  return <span className="flex items-center gap-1.5 text-zinc-400"><span style={{ background: color, width: 12, height: 12, border: "2px solid #000", display: "inline-block" }} />{label}</span>;
}
